import {
  Assessment,
  BarChart,
  DeliveryDining,
  Inbox,
  LightbulbOutlined,
  LockOutlined,
  PeopleOutline,
  Settings,
  SvgIconComponent,
} from "@mui/icons-material"
import { pathConstants } from "../../pathConstants"

export type NavItem = { labelKey: string; path: string }
export type NavGroup = {
  key: string
  labelKey: string
  icon: SvgIconComponent
  items: NavItem[]
}

/**
 * App-bar navigation, grouped like the legacy mobilus-webapp topbar (DEV-394 N3).
 * A group with one item renders as a plain link; more than one → dropdown.
 * Legacy counterparts per route: dev/projects/delivery-app-feature-matrix.md §Route map.
 */
export const navGroups: NavGroup[] = [
  {
    key: "orders",
    labelKey: "nav.orders",
    icon: Inbox,
    items: [
      { labelKey: "nav.orders", path: pathConstants.orders },
      { labelKey: "nav.recurringOrders", path: pathConstants.recurringOrders },
    ],
  },
  {
    key: "dashboard",
    labelKey: "nav.dashboard",
    icon: BarChart,
    items: [
      { labelKey: "nav.dashboard", path: pathConstants.dashboard },
      { labelKey: "nav.lockerKpis", path: pathConstants.dashboardLockers },
    ],
  },
  {
    key: "customers",
    labelKey: "nav.customer",
    icon: PeopleOutline,
    items: [
      { labelKey: "nav.customer", path: pathConstants.customers },
      { labelKey: "menu.addresses", path: pathConstants.addresses },
    ],
  },
  {
    key: "reports",
    labelKey: "nav.reports",
    icon: Assessment,
    items: [{ labelKey: "nav.reports", path: pathConstants.reports }],
  },
  {
    key: "couriers",
    labelKey: "nav.couriers",
    icon: DeliveryDining,
    items: [
      { labelKey: "nav.couriers", path: pathConstants.couriers },
      { labelKey: "menu.providerLink", path: pathConstants.links },
      { labelKey: "nav.routePlanning", path: pathConstants.routePlanning },
    ],
  },
  {
    key: "lockers",
    labelKey: "nav.lockers",
    icon: LockOutlined,
    items: [
      { labelKey: "nav.lockers", path: pathConstants.lockers },
      {
        labelKey: "nav.lockersConfiguration",
        path: pathConstants.configurationLockers,
      },
    ],
  },
  {
    key: "pick-to-light",
    labelKey: "nav.pickToLight",
    icon: LightbulbOutlined,
    items: [
      { labelKey: "nav.pickToLight", path: pathConstants.pickToLight },
      {
        labelKey: "nav.pickToLightConfiguration",
        path: pathConstants.configurationPickToLight,
      },
    ],
  },
  {
    key: "settings",
    labelKey: "nav.settings",
    icon: Settings,
    items: [
      { labelKey: "menu.configuration", path: pathConstants.configuration },
      { labelKey: "employees", path: pathConstants.employees },
      { labelKey: "nav.billing", path: pathConstants.billing },
      { labelKey: "nav.support", path: pathConstants.support },
    ],
  },
]

const allPaths = navGroups.flatMap((g) => g.items.map((i) => i.path))

/** The most specific nav path matching the location (`/` counts as Orders). */
export const findActivePath = (pathname: string): string | undefined => {
  const current = pathname === "/" ? pathConstants.orders : pathname
  return allPaths
    .filter((p) => current === p || current.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0]
}

/** `nav-{path}-link` slug: path without leading `/`, inner `/` → `-`. */
export const navSlug = (path: string) => path.slice(1).replaceAll("/", "-")
